import { Injectable } from '@angular/core';
import { appInjector } from '../app-injector';
import { SortDirection, SortType } from '../constants-types/Enums';
import { List } from '../constants-types/PiList.model';
import { HeatMapItem } from '../core-data-model/Engage.model';
import { FilterInfo, FilterType } from '../core-data-model/FilterInfo.model';
import { Job, JobMatchingPerson } from '../core-data-model/Job.model';
import { OiLink } from '../core-data-model/OiLink.model';
import { Person } from '../core-data-model/Person.model';
import { SearchResult } from '../core-data-model/SearchResult.model';
import { SearchState } from '../core-data-model/SearchState.model';
import { UserUpdate } from '../core-data-model/User.model';
import { AssessmentsApi } from '../core-services/AssessmentsApi.connect';
import { ContextDataService } from '../core-services/Context.dataservice';
import { CsvConversionService } from '../core-services/CsvConversion.service';
import { JobApi } from '../core-services/JobApi/JobApi.connect';
import { notificationOnError } from '../core-services/Notifications.service';
import { PersonApi } from '../core-services/PersonApi.connect';
import { SearchApi } from '../core-services/SearchApi.connect';
import { SearchStateFactory } from '../core-services/SearchStateFactory';
import { TableDefinitionFactoryInit } from '../core-services/TableDefinitionFactoryInit.service';
import { Table } from '../core-services/Tables/Table';
import { UrlShortenerApi } from '../core-services/UrlShortenerApi.connect';
import { UserApi } from '../core-services/UserApi.connect';
import { FeatureFlagsService } from '../system-services';

// TODO: kill this 900+ lines of boilerplate file
@Injectable({
    providedIn: 'root'
})
export class TableManagerDataService {
    constructor(
        private context: ContextDataService,
        private searchApi: SearchApi,
        private jobApi: JobApi,
        private personApi: PersonApi,
        private searchFactory: SearchStateFactory,
        private tableDefinitionFactoryInit: TableDefinitionFactoryInit,
        private urlShortenerApi: UrlShortenerApi,
        private userApi: UserApi,
        private assessmentsApi: AssessmentsApi,
        private featureFlagService: FeatureFlagsService
    ) {}

    makeFolderTable() {
        return new FolderTableData(
            this.searchApi,
            this.searchFactory,
            this.tableDefinitionFactoryInit
        );
    }

    makeJobDashboardOverviewTable() {
        return new JobDashboardOverviewTableData(
            this.jobApi,
            this.context,
            this.searchFactory,
            this.tableDefinitionFactoryInit
        );
    }

    makeJobDashboardOverviewExtraMatchesTable() {
        return new JobDashboardOverviewExtraMatchesTableData(
            this.jobApi,
            this.context,
            this.searchFactory
        );
    }

    makePersonDashboardOverviewExtraJobMatchesTable() {
        return new PersonDashboardOverviewExtraJobMatchesTableData(
            this.personApi,
            this.context,
            this.searchFactory
        );
    }

    makeEmployeeDirectoryTable() {
        return new EmployeeDirectoryTableData(
            this.searchApi,
            this.searchFactory,
            this.tableDefinitionFactoryInit
        );
    }

    makeJobsDirectoryTable() {
        return new JobsDirectoryTableDataWithoutSearch(
            this.searchApi,
            this.searchFactory,
            this.tableDefinitionFactoryInit
        );
    }

    makePendingAssessmentsTable() {
        return new PendingAssessmentsTableData(
            this.searchApi,
            this.searchFactory,
            this.tableDefinitionFactoryInit
        );
    }

    makeRecentlyCompletedTable() {
        return new RecentlyCompletedTableData(
            this.searchApi,
            this.searchFactory,
            this.tableDefinitionFactoryInit
        );
    }

    makeSearchResultsTable() {
        return new SearchResultsTableData(
            this.searchApi,
            this.searchFactory,
            this.tableDefinitionFactoryInit
        );
    }

    makeAssessmentsLinkTable() {
        return new AssessmentsLinkTableData(
            this.urlShortenerApi,
            this.searchFactory
        );
    }

    makeOI1AssessmentsLinkTable() {
        return new OI1AssessmentsLinkTableData(
            this.urlShortenerApi,
            this.searchFactory
        );
    }

    makeRecentlyCompletedCardTable(completedCardAdministeredByMe: boolean) {
        return new RecentlyCompletedCardTableData(
            this.searchFactory,
            this.assessmentsApi,
            completedCardAdministeredByMe
        );
    }

    makePendingAssessmentsCardTable(pendingCardAdministeredByMe: boolean) {
        return new PendingAssessmentsCardTableData(
            this.searchFactory,
            this.assessmentsApi,
            pendingCardAdministeredByMe
        );
    }

    makeRecentJobActivityCardTable() {
        return new RecentJobActivityCardTableData(
            this.searchApi,
            this.searchFactory
        );
    }

    makeMultipleContributorReviewTable() {
        return new MultipleContributorReviewTableData();
    }

    makeMultipleContributorReviewCognitiveTable() {
        return new MultipleContributorReviewCognitiveTableData();
    }

    makeInterviewCandidatesTable() {
        return new InterviewCandidatesTableData(
            this.searchFactory,
            this.context,
            this.jobApi
        );
    }

    makeInterviewCandidatesJobTable() {
        return new InterviewCandidatesJobTableWithoutSearch(
            this.searchApi,
            this.searchFactory
        );
    }

    makeInterviewGuideReminderTable() {
        return new BasicContextView(this.context);
    }

    makeUpdateFolderTable() {
        return new BasicContextView(this.context);
    }
    makeUpdateGroupTable() {
        return new BasicContextView(this.context);
    }

    makeUpdateJobTable() {
        return new PersonContextView(this.context);
    }
    makeUpdateTypeTable() {
        return new PersonContextView(this.context);
    }

    makeHeatMapTable(heatMapItems: HeatMapItem[]) {
        return new HeatMapTableData(heatMapItems);
    }

    makeUsersTable(isThirdPartyUsers = false, isSharingPanel = false) {
        return new UsersTableData(
            this.searchFactory,
            this.userApi,
            isThirdPartyUsers,
            isSharingPanel
        );
    }
}

export class FolderTableData extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory,
        tableDefinitionFactoryInit: TableDefinitionFactoryInit
    ) {
        super('folderPage');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .addSort(SortType.Name);
        this.possibleFilters = tableDefinitionFactoryInit.SearchFilters;
    }

    @notificationOnError()
    protected internalSearch(searchState: SearchState) {
        return this.searchApi.searchPeopleAndJobs(searchState);
    }
}

// TODO Potentially move Job out of context
export class JobDashboardOverviewTableData
    extends Table<SearchResult>
    implements OverrideDefaultPageSizeTableSearchResult
{
    constructor(
        private jobApi: JobApi,
        private context: ContextDataService,
        private searchFactory: SearchStateFactory,
        tableDefinitionFactoryInit: TableDefinitionFactoryInit
    ) {
        super('jobDashboardOverview');
        this.contents = [];
        this.searchState = searchFactory.newSearchState().setPageSize(50);
        this.possibleFilters = tableDefinitionFactoryInit.JobMatchesFilters;
    }

    protected internalSearch(searchState: SearchState) {
        return this.jobApi.getJobMatches(this.context.job.id, searchState);
    }

    prepCsv(results: List<SearchResult>, job: Job): any[] {
        const csv: CsvConversionService =
            appInjector().get(CsvConversionService);
        return results
            .map((result) => {
                const retval: any = {};
                csv.addResultNameColumn(retval, result);
                csv.addEmailColumn(retval, result);
                csv.addPersonTypeColumn(retval, result);
                csv.addMatchFavoriteColumn(retval, result);
                csv.addMatchOverallCompatibilityScoreColumn(
                    retval,
                    result,
                    job
                );
                csv.addBehavioralColumn(retval, result);
                csv.addMatchBehavioralScoreColumn(retval, result);
                csv.addReferenceProfileNameColumn(retval, result);
                csv.addCognitiveColumn(retval, result);
                csv.addMatchCognitiveScoreColumn(retval, result, job);
                csv.addLastUpdatedColumn(retval, result);
                csv.addResultBehaviorScoreIdColumn(retval, result);
                return retval;
            })
            .toArray();
    }

    overrideDefaultPageSize(defaultPageSize: number) {
        this.searchState = this.searchFactory
            .newSearchState()
            .setPageSize(defaultPageSize);
    }
}

export interface OverrideDefaultPageSizeTableSearchResult
    extends Table<SearchResult> {
    overrideDefaultPageSize(defaultPageSize: number): void;
}

export class JobDashboardOverviewExtraMatchesTableData extends Table<Person> {
    constructor(
        private jobApi: JobApi,
        private context: ContextDataService,
        searchFactory: SearchStateFactory
    ) {
        super('jobDashboardOverviewExtraMatches');
        this.contents = [];
        this.searchState = searchFactory.newSearchState().setPageSize(10);
        this.possibleFilters = [];
    }

    protected internalSearch(searchState: SearchState) {
        return this.jobApi.getPeopleWhoMatchJob(
            this.context.job.id,
            searchState
        );
    }
}

export class PersonDashboardOverviewExtraJobMatchesTableData extends Table<JobMatchingPerson> {
    constructor(
        private personApi: PersonApi,
        private context: ContextDataService,
        searchFactory: SearchStateFactory
    ) {
        super('personDashboardOverviewExtraJobMatches');
        this.contents = [];
        this.searchState = searchFactory.newSearchState().setPageSize(10);
        this.possibleFilters = [];
    }

    protected internalSearch(searchState: SearchState) {
        return this.personApi.getJobsThatMatchPerson(
            this.context.person.id,
            searchState
        );
    }
}

export class EmployeeDirectoryTableData extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory,
        tableDefinitionFactoryInit: TableDefinitionFactoryInit
    ) {
        super('employeeDirectory');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .addFilter(new FilterInfo({ type: FilterType.TypeEmployee }))
            .addSort(SortType.Name);
        this.possibleFilters =
            tableDefinitionFactoryInit.EmployeeDirectoryFilters;
    }

    @notificationOnError()
    protected internalSearch(searchState: SearchState) {
        return this.searchApi.searchPeopleAndJobs(searchState);
    }

    prepCsv(results: List<SearchResult>, _job: Job): any[] {
        const csv: CsvConversionService =
            appInjector().get(CsvConversionService);
        return results
            .map((result) => {
                const retval: any = {};
                csv.addResultTypeColumn(retval, result);
                csv.addResultNameColumn(retval, result);
                csv.addEmailColumn(retval, result);
                csv.addResultJobTitleColumn(retval, result);
                csv.addLastUpdatedColumn(retval, result);
                csv.addBehavioralColumn(retval, result);
                csv.addReferenceProfileNameColumn(retval, result);
                csv.addResultBehaviorScoreIdColumn(retval, result);
                csv.addAdministeredByBehavioralColumn(retval, result);
                csv.addAdministeredByCognitiveColumn(retval, result);
                return retval;
            })
            .toArray();
    }
}

export class JobsDirectoryTableData extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory,
        tableDefinitionFactoryInit: TableDefinitionFactoryInit
    ) {
        super('jobsDirectory');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .addFilter(new FilterInfo({ type: FilterType.Jobs }))
            .addSort(SortType.RecentlyModifiedJob, SortDirection.Descending);
        this.possibleFilters = tableDefinitionFactoryInit.JobsDirectoryFilters;
    }

    @notificationOnError()
    protected internalSearch(searchState: SearchState) {
        return this.searchApi.searchPeopleAndJobsWithJobHealth(searchState);
    }

    prepCsv(results: List<SearchResult>, _job: Job): any[] {
        const csv: CsvConversionService =
            appInjector().get(CsvConversionService);
        return results
            .map((result) => {
                const retval: any = {};
                csv.addResultTypeColumn(retval, result);
                csv.addResultNameColumn(retval, result);
                csv.addJobPipelineHealth(retval, result);
                csv.addLastUpdatedColumn(retval, result);
                return retval;
            })
            .toArray();
    }
}

export class JobsDirectoryTableDataWithoutSearch extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory,
        tableDefinitionFactoryInit: TableDefinitionFactoryInit
    ) {
        super('jobsDirectory');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .addFilter(new FilterInfo({ type: FilterType.Jobs }))
            .addSort(SortType.RecentlyModifiedJob, SortDirection.Descending);
        this.possibleFilters = tableDefinitionFactoryInit.JobsDirectoryFilters;
    }

    @notificationOnError()
    protected internalSearch(searchState: SearchState) {
        return this.searchApi.searchJobsApiWithJobHealth(searchState);
    }

    prepCsv(results: List<SearchResult>, _job: Job): any[] {
        const csv: CsvConversionService =
            appInjector().get(CsvConversionService);
        return results
            .map((result) => {
                const retval: any = {};
                csv.addResultTypeColumn(retval, result);
                csv.addResultNameColumn(retval, result);
                csv.addJobPipelineHealth(retval, result);
                csv.addLastUpdatedColumn(retval, result);
                return retval;
            })
            .toArray();
    }
}

export class PendingAssessmentsTableData extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory,
        tableDefinitionFactoryInit: TableDefinitionFactoryInit
    ) {
        super('pendingAssessments');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .addFilter(
                new FilterInfo({ type: FilterType.SomeAssessmentsPending })
            )
            .addSort(SortType.RecentlySent, SortDirection.Descending);
        this.possibleFilters = tableDefinitionFactoryInit.PendingPageFilters;
    }

    @notificationOnError()
    protected internalSearch(searchState: SearchState) {
        return this.searchApi.searchPeopleAndJobs(searchState);
    }

    prepCsv(results: List<SearchResult>, _job: Job): any[] {
        const csv: CsvConversionService =
            appInjector().get(CsvConversionService);
        return results
            .map((result) => {
                const retval: any = {};
                csv.addResultTypeColumn(retval, result);
                csv.addResultNameColumn(retval, result);
                csv.addEmailColumn(retval, result);
                csv.addResultJobTitleColumn(retval, result);
                csv.addLastSentColumn(retval, result);
                csv.addPersonTypeColumn(retval, result);
                csv.addBehavioralColumn(retval, result);
                csv.addCognitiveColumn(retval, result);
                csv.addResultBehaviorScoreIdColumn(retval, result);
                csv.addAdministeredByBehavioralColumn(retval, result);
                csv.addAdministeredByCognitiveColumn(retval, result);
                return retval;
            })
            .toArray();
    }
}

export class RecentlyCompletedTableData extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory,
        tableDefinitionFactoryInit: TableDefinitionFactoryInit
    ) {
        super('recentlyCompleted');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .addFilter(
                new FilterInfo({ type: FilterType.SomeAssessmentsCompleted })
            )
            .addSort(SortType.RecentlyCompleted, SortDirection.Descending);
        this.possibleFilters =
            tableDefinitionFactoryInit.RecentlyCompletedPageFilters;
    }

    @notificationOnError()
    protected internalSearch(searchState: SearchState) {
        return this.searchApi.searchPeopleAndJobs(searchState);
    }

    prepCsv(results: List<SearchResult>, _job: Job): any[] {
        const csv: CsvConversionService =
            appInjector().get(CsvConversionService);
        return results
            .map((result) => {
                const retval: any = {};
                csv.addResultTypeColumn(retval, result);
                csv.addResultNameColumn(retval, result);
                csv.addEmailColumn(retval, result);
                csv.addResultJobTitleColumn(retval, result);
                csv.addLastCompletedColumn(retval, result);
                csv.addFolderColumn(retval, result);
                csv.addPersonTypeColumn(retval, result);
                csv.addBehavioralColumn(retval, result);
                csv.addReferenceProfileNameColumn(retval, result);
                csv.addResultBehaviorScoreIdColumn(retval, result);
                csv.addCognitiveColumn(retval, result);
                csv.addAdministeredByBehavioralColumn(retval, result);
                csv.addAdministeredByCognitiveColumn(retval, result);
                return retval;
            })
            .toArray();
    }
}

export class SearchResultsTableData extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory,
        tableDefinitionFactoryInit: TableDefinitionFactoryInit
    ) {
        super('searchResults');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .addSort(SortType.Name);
        this.possibleFilters = tableDefinitionFactoryInit.SearchFilters;
    }

    @notificationOnError()
    protected internalSearch(searchState: SearchState) {
        return this.searchApi.searchPeopleAndJobs(searchState);
    }
}

export class CsvShortcuts {}

export class AssessmentsLinkTableData extends Table<OiLink> {
    constructor(
        private urlShortenerApi: UrlShortenerApi,
        searchFactory: SearchStateFactory
    ) {
        super('assessmentLinks');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .setPageSize(50)
            .addSort(SortType.Name);
    }

    // TODO - take a look at this - weird to pass a search state and not use it
    @notificationOnError()
    protected internalSearch() {
        return this.urlShortenerApi.getAllShortenedUrls(this.searchState, true);
    }
}

export class OI1AssessmentsLinkTableData extends Table<OiLink> {
    constructor(
        private urlShortenerApi: UrlShortenerApi,
        searchFactory: SearchStateFactory
    ) {
        super('oi1AssessmentLinks');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .setPageSize(50)
            .addSort(SortType.Name);
    }

    // TODO - take a look at this - weird to pass a search state and not use it
    protected internalSearch() {
        return this.urlShortenerApi.getAllOi1Urls(this.searchState);
    }
}

export class RecentlyCompletedCardTableData extends Table<Person> {
    constructor(
        searchFactory: SearchStateFactory,
        private assessmentsApi: AssessmentsApi,
        private completedCardAdministeredByMe: boolean
    ) {
        super('recentlyCompletedCard');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .setPageSize(5)
            .setFilter(
                new FilterInfo({ type: FilterType.SomeAssessmentsCompleted })
            )
            .addSort(SortType.RecentlyCompleted, SortDirection.Descending);
    }

    @notificationOnError()
    protected internalSearch() {
        const result =
            this.assessmentsApi.getRecentCompletedBehavioralAssessments(
                this.completedCardAdministeredByMe
            );

        // needed to display "No Results Found" in the component file
        // since it is coniditioned upon this.searchState.pageState.maxResults
        result.then((value) => {
            if (value.length === 0) {
                this.searchState.pageState.maxResults = 0;
            }
        });
        return result;
    }
}

export class PendingAssessmentsCardTableData extends Table<Person> {
    constructor(
        searchFactory: SearchStateFactory,
        private assessmentsApi: AssessmentsApi,
        private completedCardAdministeredByMe: boolean
    ) {
        super('pendingAssessmentsCard');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .setPageSize(5)
            .setFilter(
                new FilterInfo({ type: FilterType.AllAssessmentsPending })
            )
            .addSort(SortType.RecentlySent, SortDirection.Descending);
    }

    @notificationOnError()
    protected internalSearch() {
        const result =
            this.assessmentsApi.getRecentPendingBehavioralAssessments(
                this.completedCardAdministeredByMe
            );

        // needed to display "No Results Found" in the component file
        // since it is coniditioned upon this.searchState.pageState.maxResults
        result.then((value) => {
            if (value.length === 0) {
                this.searchState.pageState.maxResults = 0;
            }
        });
        return result;
    }
}

export class RecentJobActivityCardTableData extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory
    ) {
        super('activeJobCard');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .setPageSize(5)
            .setFilter(new FilterInfo({ type: FilterType.Jobs }))
            .addSort(SortType.RecentlyModifiedJob, SortDirection.Descending);
    }

    protected internalSearch(searchState: SearchState) {
        return this.searchApi.searchPeopleAndJobsWithJobHealth(searchState);
    }
}

export class MultipleContributorReviewTableData extends Table<SearchResult> {
    constructor() {
        super('multipleContributorReview');
    }
}

export class MultipleContributorReviewCognitiveTableData extends Table<SearchResult> {
    constructor() {
        super('multipleContributorReviewCog');
    }
}

export class InterviewCandidatesTableData extends Table<SearchResult> {
    constructor(
        searchFactory: SearchStateFactory,
        private context: ContextDataService,
        private jobApi: JobApi
    ) {
        super('interviewCandidatesTable');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .setPageSize(50)
            .addFilter(new FilterInfo({ type: FilterType.TypeCandidate }));
    }

    protected internalSearch() {
        return this.jobApi.getJobMatches(this.context.job.id, this.searchState);
    }
}
export class InterviewCandidatesJobTable extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory
    ) {
        super('interviewCandidatesJobTable');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .addFilter(new FilterInfo({ type: FilterType.Jobs }))
            .setPageSize(5)
            .addSort(SortType.RecentlyModifiedJob, SortDirection.Descending);
    }

    protected internalSearch() {
        return this.searchApi.searchPeopleAndJobsWithJobHealth(
            this.searchState
        );
    }
}

export class InterviewCandidatesJobTableWithoutSearch extends Table<SearchResult> {
    constructor(
        private searchApi: SearchApi,
        searchFactory: SearchStateFactory
    ) {
        super('interviewCandidatesJobTable');
        this.contents = [];
        this.searchState = searchFactory
            .newSearchState()
            .addFilter(new FilterInfo({ type: FilterType.Jobs }))
            .setPageSize(5)
            .addSort(SortType.RecentlyModifiedJob, SortDirection.Descending);
    }

    protected internalSearch() {
        return this.searchApi.searchJobsApiWithJobHealth(this.searchState);
    }
}

export class BasicContextView extends Table<SearchResult> {
    constructor(private context: ContextDataService) {
        super('basic');
    }

    // TODO - add some sort of caching is this is costly
    get contents() {
        return this.context.currentSet.toArray();
    }

    set contents(value) {
        throw new Error('Can not set!' + value);
    }
}
export class PersonContextView extends Table<SearchResult> {
    constructor(private context: ContextDataService) {
        super('basic');
    }

    // TODO - add some sort of caching is this is costly
    get contents() {
        return this.context.currentSet
            .filter((row): row is Person => row instanceof Person)
            .toArray();
    }

    set contents(value) {
        throw new Error('Can not set!' + value);
    }
}

export class HeatMapTableData extends Table<HeatMapItem> {
    constructor(heatMapItems: HeatMapItem[]) {
        super('heatMapTable');
        this.contents = heatMapItems;
    }
}

export class UsersTableData extends Table<UserUpdate> {
    constructor(
        searchFactory: SearchStateFactory,
        private userApi: UserApi,
        private isThirdPartyUsers: boolean,
        private isSharingPanel: boolean = false
    ) {
        super('usersTable');
        this.searchState = searchFactory
            .newSearchState()
            .setPageSize(10)
            .addSort(SortType.Name, SortDirection.Ascending);
        if (!isThirdPartyUsers || isSharingPanel) {
            this.searchState = this.searchState.addFilter(
                new FilterInfo({
                    type: FilterType.isAccessEnabled
                })
            );
        }
    }
    @notificationOnError()
    protected internalSearch(searchState: SearchState) {
        return this.userApi
            .searchUsers(
                searchState,
                this.isThirdPartyUsers ? 'thirdParty' : 'users'
            )
            .then((res) => res.map((x) => new UserUpdate(x)));
    }
}
